import React, { FC, useState } from 'react';
import styled from 'styled-components';
import truncate from 'lodash/truncate';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MaterialTooltip from '@mui/material/Tooltip';

import { useMountedEffect } from '@clatter/platform';
import { FolderManagerBreadcrumb, FolderManagerBreadcrumbsProps } from '../../../interfaces';
import chevronIcon from '../../../../assets/icons/chevron-light.svg';

//region STYLED
const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme?.palette?.text};
`;

const StyledBreadcrumbItem = styled.h5`
  position: relative;
  cursor: pointer;
  font-size: ${({ theme }) => theme?.fontSize.sectionHeader};
  margin: 0;

  display: flex;
  align-items: center;

  &:not(:first-child) {
    color: ${({ theme }) => theme?.palette?.grayLighter};
  }

  &:hover {
    text-decoration: underline;
  }

  &:last-child {
    cursor: default;

    &:hover {
      text-decoration: none;
    }
  }

  .grayed {
    filter: brightness(1.5);
  }
`;
//endregion

const StyledDivider = styled.span`
  display: inline-block;

  svg {
    width: 20px;
    height: 20px;
    stroke: ${({ theme }) => theme?.palette?.grayLighter};
  }
`;

const BreadcrumbTitle = ({ title, maxLength = 30 }: { title: string; maxLength?: number }) => (
  <MaterialTooltip title={title} disableHoverListener={title?.length <= maxLength}>
    <span>{truncate(title, { length: maxLength })}</span>
  </MaterialTooltip>
);

export const NgBreadcrumbs: FC<FolderManagerBreadcrumbsProps> = ({ data, maxLength, onClick }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.currentTarget.innerText === '...' && !anchorEl) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClickItem = (data: any) => {
    if (typeof onClick === 'function') {
      onClick(data);
    }
  };

  const handleClose = (data: { item: FolderManagerBreadcrumb; index: number } | null) => {
    if (data && typeof onClick === 'function') {
      onClick(data);
    }
    setAnchorEl(null);
  };

  useMountedEffect(() => {
    return () => handleClose(null);
  }, []);

  return (
    <StyledContainer>
      {data?.length > 2 ? (
        <>
          <StyledBreadcrumbItem onClick={(event) => handleClick(event)}>...</StyledBreadcrumbItem>

          <Menu id="basic-menu" open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => handleClose(null)}>
            {(data || []).map((item, index) => (
              <MenuItem key={`${index}-${item.name}`} onClick={() => handleClose({ item, index })}>
                {item.name}
              </MenuItem>
            ))}
          </Menu>

          <StyledDivider>
            <img src={chevronIcon} alt=">" />
          </StyledDivider>

          <StyledBreadcrumbItem
            key={data[data.length - 2]?.id}
            onClick={() =>
              handleClickItem({
                item: data[data.length - 2],
                index: data.length - 2,
              })
            }
          >
            <BreadcrumbTitle maxLength={maxLength} title={data[data.length - 2]?.name} />
          </StyledBreadcrumbItem>

          <StyledDivider>
            <img src={chevronIcon} alt=">" />
          </StyledDivider>

          <StyledBreadcrumbItem
            key={data[data.length - 1]?.id}
            onClick={() =>
              handleClickItem({
                item: data[data.length - 1],
                index: data.length - 1,
              })
            }
          >
            <BreadcrumbTitle maxLength={maxLength} title={data[data.length - 1]?.name} />
          </StyledBreadcrumbItem>
        </>
      ) : (
        (data || []).map((item, index) => (
          <React.Fragment key={item.id || `${index}-${item.name}`}>
            <StyledBreadcrumbItem onClick={() => (index < data.length - 1 ? handleClickItem({ item, index }) : {})}>
              <BreadcrumbTitle maxLength={maxLength} title={item?.name} />
            </StyledBreadcrumbItem>

            {index !== data?.length - 1 && (
              <StyledDivider>
                <img src={chevronIcon} alt=">" />
              </StyledDivider>
            )}
          </React.Fragment>
        ))
      )}
    </StyledContainer>
  );
};
